import { Button, Typography } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import PatientDetailsComponent from "./components/layouts/PatientDetailsComponent";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
} from "react";
import { EmrContextType, initialData } from "./types";
import { HL7ContexTriggerContex } from "../context/HL7MessageTrigger/HL7MessageTriggerProvider";
import { AllergyProvider } from "./components/Allergy";
import { useFetchPatientDetailsByEMR } from "../PatientCare/hooks/apiHooks";
import GrantsForDental from "./components/DentalProcedures/grantsForDental";
import patientCareRouteList from "../PatientCare/routeList";
import moduleRoutes from "../routes/moduleRouteList";
import { base } from "../constants";
export const EmrContext = createContext<EmrContextType>({
  patientData: initialData,
  dataLoading: false,
  refetcPatientData: () => { },
  page:null,
  triggerConsultationStart: () => {}
});

import { consultationStartService } from "./services";
import moment from "moment";

export const EmrDataProvider: FC<PropsWithChildren> = ({ children }) => {
  const { encounterId } = useParams();
  const {
    mutate: fetchPatientDetails,
    data,
    isLoading,
  } = useFetchPatientDetailsByEMR({});
  const [searchParams] = useSearchParams();
  const page = searchParams.get("page") || null;
  const refetcPatientData = () => {
    if (encounterId) {
      const emrNo = searchParams.get("emrno") || "";
      fetchPatientDetails({ encounterId: Number(encounterId || "0"), emrNo });
    }
  };

  const triggerConsultationStart = () => {
    if (!encounterId) return;
    
    const storageKey = `emr_started_${encounterId}`;
    const isStarted = localStorage.getItem(storageKey);
    
    if (!isStarted) {
      consultationStartService({
        encounterId: Number(encounterId),
        destination: "EMR started",
        sourceDateTime: moment().format("YYYY-MM-DDTHH:mm:ss")
      }).then((res) => {
        if (res.responseIndicator === "success") {
          localStorage.setItem(storageKey, "true");
        }
      });
    }
  };

  useEffect(() => {
    if (encounterId) {
      const emrNo = searchParams.get("emrno") || "";
      localStorage.removeItem(`emr_started_${encounterId}`);
      fetchPatientDetails({ encounterId: Number(encounterId || "0"), emrNo });
    }
  }, [encounterId]);
  return (
    <EmrContext.Provider
      value={{
        patientData: data?.response || initialData,
        dataLoading: isLoading,
        refetcPatientData,
        page,
        triggerConsultationStart
      }}
    >
      {children}
    </EmrContext.Provider>
  );
};

const PatientHistoryButton = () => {
  const navigate = useNavigate();
  const { patientData } = useContext(EmrContext);
  const patientMasterId = patientData?.pmaster?.id;
  const patientId = patientData?.pmaster?.patientId;

  const handleViewHistory = () => {
    if (!patientMasterId) return;
    navigate(
      `${base}${moduleRoutes.mainRoute}/patient-care/${patientCareRouteList.patientDetailsHistory}/${patientMasterId}/${patientId}`,
    );
  };

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HistoryIcon />}
      onClick={handleViewHistory}
      disabled={!patientMasterId}
    >
      Patient History
    </Button>
  );
};

const EMR = () => {
  const { triggerApi } = useContext(HL7ContexTriggerContex);

  useEffect(() => {
    return () => {
      triggerApi();
    };
  }, []);

  return (
    <EmrDataProvider>
      <AllergyProvider>
        <div className="pb-0 px-2 pt-2 h-fit max-h-screen flex flex-col gap-2">
          <div className="flex items-center justify-between px-2">
            <Typography variant="h6" fontSize={"1rem"}>
              Patient care - EMR
            </Typography>
            <PatientHistoryButton />
          </div>
          <PatientDetailsComponent />
          <GrantsForDental />
        </div>
      </AllergyProvider>
    </EmrDataProvider>
  );
};

export default EMR;
